import { forwardRef } from 'react';
import {
  CircularProgress,
  type ButtonProps as BaseButtonProps,
} from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import { Spacing } from '@mui/system';

import { StyledButton } from './styled-button';

const getSizeMapping = (spacing: Spacing, typography: Theme['typography']) => ({
  sm: {
    fontSize: typography.textSm.fontSize,
    padding: spacing(2, 3.5),
  },
  md: {
    fontSize: typography.textSm.fontSize,
    padding: spacing(2.5, 4),
  },
  lg: {
    fontSize: typography.textMd.fontSize,
    padding: spacing(2.5, 4.5),
  },
  xl: {
    fontSize: typography.textMd.fontSize,
    padding: spacing(3, 5),
  },
  '2xl': {
    fontSize: typography.textLg.fontSize,
    padding: spacing(4, 7),
  },
});

interface ButtonProps extends BaseButtonProps {
  to?: string;
  isLoading?: boolean;
  loadingPosition?: 'start' | 'end' | 'center';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      padding,
      size = 'md' as const,
      isLoading,
      loadingPosition = 'start',
      ...rest
    }: ButtonProps,
    ref,
  ) => {
    const { spacing, typography } = useTheme();
    const sizeMapping = getSizeMapping(spacing, typography)[size];

    const progressEl = <CircularProgress size={20} sx={{ p: 0.5 }} />;
    const loadingProps = {
      disabled: isLoading ? true : rest.disabled,
      endIcon:
        loadingPosition === 'end' && isLoading ? progressEl : rest.endIcon,
      startIcon:
        loadingPosition === 'start' && isLoading ? progressEl : rest.startIcon,
    };

    return (
      <StyledButton
        {...rest}
        fontSize={sizeMapping.fontSize}
        padding={padding || sizeMapping.padding}
        disableFocusRipple
        disableRipple
        ref={ref}
        {...loadingProps}
      >
        {children}
      </StyledButton>
    );
  },
);
